import { useEffect, useState, useCallback } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  IconUser,
  IconLoader2,
  IconArrowLeft,
  IconDeviceFloppy,
  IconClipboardList,
  IconAlertTriangle,
  IconPlus,
} from "@tabler/icons-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { api, apiClient } from "@/lib/api"
import { toast } from "sonner"
import { formatPersonName } from "@/lib/person-identity"

// ── Types ────────────────────────────────────────────────────────────

interface PersonDto {
  id: string
  lastName: string
  firstName: string
  middleName: string
  socialSecurityNumber?: string
  gender?: number
  birthDate: string
  ethnicity?: number
  relationship?: number
  sourceOfIncome?: number
  workStatus?: number
  otherCharacteristics: string
  medicalInsurance?: number
  educationLevel?: number
  eligibilityStatus?: number
  incomeCollection: unknown[]
}

interface BenefitApplicationSummaryDto {
  id: string
  programType: number
  programYear: number
  status: number
  intakeDate: string
  certificationDate: string | null
  amountPaid: number
  amountEligible: number
  peopleInHousehold: number
  isApplicant: boolean
  applicantName: string
  address: string
}

interface ChangeRequestDto {
  id: number
  status: string
  createdByName: string
  assignedToName: string
  createdAt: string
}

const genderOptions = [
  { value: "0", label: "Female" },
  { value: "1", label: "Male" },
  { value: "2", label: "Trans-Male" },
  { value: "3", label: "Trans-Female" },
  { value: "4", label: "Gender Non-Conforming" },
  { value: "5", label: "Unknown" },
]

const genderLabels: Record<number, string> = {
  0: "Female",
  1: "Male",
  2: "Trans-Male",
  3: "Trans-Female",
  4: "Gender Non-Conforming",
  5: "Unknown",
}

const statusLabels: Record<number, string> = {
  0: "Draft",
  5: "Certified (Legacy)",
  6: "In Review",
  10: "Completed",
  20: "Proofed",
  30: "Certified",
  100: "Inactive",
  110: "Pre-Season",
  120: "Denied",
  200: "Submitted",
  255: "Draft",
}

const benefitTypeLabels: Record<number, string> = {
  0: "PSE",
  1: "LIHEAP",
  2: "Lakehaven",
  3: "SHEAP",
  100: "Combined",
}

// ── Component ────────────────────────────────────────────────────────

export default function PersonDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [person, setPerson] = useState<PersonDto | null>(null)
  const [benefits, setBenefits] = useState<BenefitApplicationSummaryDto[]>([])
  const [benefitsLoading, setBenefitsLoading] = useState(true)
  const [pendingRequests, setPendingRequests] = useState<ChangeRequestDto[]>([])

  // Form state
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    socialSecurityNumber: "",
    birthDate: "",
    gender: "",
  })

  const loadPerson = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await api.get<PersonDto>(
        `/api/people/${id}?includeDemographics=true`
      )
      setPerson(data)
      setForm({
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName ?? "",
        socialSecurityNumber: data.socialSecurityNumber ?? "",
        birthDate: data.birthDate,
        gender: data.gender?.toString() ?? "",
      })
    } catch (err) {
      console.error("Failed to load person:", err)
      toast.error("Failed to load person information.")
    } finally {
      setLoading(false)
    }
  }, [id])

  const loadBenefits = useCallback(async () => {
    if (!id) return
    setBenefitsLoading(true)
    try {
      const data = await api.get<BenefitApplicationSummaryDto[]>(
        `/api/people/${id}/benefit-applications`
      )
      setBenefits(data)
    } catch (err) {
      console.error("Failed to load benefits:", err)
    } finally {
      setBenefitsLoading(false)
    }
  }, [id])

  // The applicant is already known here, so the guided flow opens past its first step.
  const handleCreateApplication = useCallback(() => {
    if (!id) return
    navigate(`/benefit-applications/new?applicantId=${id}&step=applications`)
  }, [id, navigate])

  const loadPendingRequests = useCallback(async () => {
    if (!id) return
    try {
      const data = await api.get<ChangeRequestDto[]>(
        `/api/people/change-requests/by-person/${id}`
      )
      setPendingRequests(data)
    } catch {
      // Not critical — ignore
    }
  }, [id])

  useEffect(() => {
    loadPerson()
    loadBenefits()
    loadPendingRequests()
  }, [loadPerson, loadBenefits, loadPendingRequests])

  const handleSave = async () => {
    if (!id || !person) return
    if (!form.firstName.trim() || !form.lastName.trim() || !form.birthDate || !form.gender) {
      toast.error("Please fill in all required fields.")
      return
    }

    setSaving(true)
    try {
      const response = await apiClient(`/api/people/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          id,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          middleName: form.middleName.trim() || null,
          socialSecurityNumber: form.socialSecurityNumber.trim() || null,
          birthDate: form.birthDate,
          gender: parseInt(form.gender),
        }),
      })

      if (response.status === 202) {
        toast.info("Changes saved but require review approval since this person has certified benefit grants.")
        loadPendingRequests()
      } else if (response.ok) {
        toast.success("Person updated successfully.")
      } else if (response.status === 409) {
        const body = await response.json().catch(() => ({}))
        if (body.title === "Pending change request") {
          toast.error("This person has a pending change request that must be reviewed before further updates can be made.")
        } else {
          toast.error("Person with that social security number already exists.")
        }
      } else {
        toast.error(`Failed to save: ${response.status}`)
      }
      loadPerson()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save."
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—"
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const hasChanges =
    person &&
    (form.firstName !== person.firstName ||
      form.lastName !== person.lastName ||
      form.middleName !== (person.middleName ?? "") ||
      form.socialSecurityNumber !== (person.socialSecurityNumber ?? "") ||
      form.birthDate !== person.birthDate ||
      form.gender !== (person.gender?.toString() ?? ""))

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!person) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Person not found.</p>
        <Link to="/people" className="text-primary hover:underline mt-2 inline-block">
          ← Back to People
        </Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/people"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <IconArrowLeft className="size-5" />
          </Link>
          <IconUser className="size-6" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {formatPersonName(person)}
          </h1>
        </div>
        <Button onClick={handleSave} disabled={saving || !hasChanges || pendingRequests.length > 0}>
          {saving ? (
            <IconLoader2 className="size-4 mr-2 animate-spin" />
          ) : (
            <IconDeviceFloppy className="size-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Pending Change Request Banner */}
      {pendingRequests.length > 0 && (
        <Alert variant="default" className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
          <IconAlertTriangle className="size-4 text-yellow-600" />
          <AlertTitle>Pending Change Review</AlertTitle>
          <AlertDescription>
            This person has {pendingRequests.length} pending change{" "}
            {pendingRequests.length === 1 ? "request" : "requests"} awaiting
            review. Further updates are blocked until the pending{" "}
            {pendingRequests.length === 1 ? "request is" : "requests are"}{" "}
            approved or rejected.
            {pendingRequests.map((r) => (
              <span key={r.id} className="block text-xs mt-1 text-muted-foreground">
                Requested by {r.createdByName} · Assigned to {r.assignedToName}
              </span>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Person Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>
            Edit the person&apos;s demographic information below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, firstName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lastName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="middleName">Middle Name</Label>
              <Input
                id="middleName"
                value={form.middleName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, middleName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ssn">Social Security Number</Label>
              <Input
                id="ssn"
                value={form.socialSecurityNumber}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    socialSecurityNumber: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dob">Date of Birth</Label>
              <Input
                id="dob"
                type="date"
                value={form.birthDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, birthDate: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select
                value={form.gender}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, gender: v }))
                }
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  {genderOptions.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benefit Applications Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <IconClipboardList className="size-5" />
              <CardTitle>Benefit Applications</CardTitle>
            </div>
            <Button size="sm" onClick={handleCreateApplication}>
              <IconPlus className="size-4" />
              Create New Benefit
            </Button>
          </div>
          <CardDescription>
            Benefit applications this person has been part of.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {benefitsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : benefits.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No benefit applications found for this person.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Program</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Intake Date</TableHead>
                    <TableHead>Certification Date</TableHead>
                    <TableHead>Applicant</TableHead>
                    <TableHead className="text-right">Amount Paid</TableHead>
                    <TableHead className="text-right">Amount Eligible</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benefits.map((benefit) => (
                    <TableRow key={benefit.id}>
                      <TableCell>
                        <Link
                          to={`/benefit-applications/${benefit.id}`}
                          className="text-primary hover:underline font-medium"
                        >
                          {benefitTypeLabels[benefit.programType] ?? "Unknown"}
                        </Link>
                      </TableCell>
                      <TableCell>{benefit.programYear}</TableCell>
                      <TableCell>
                        <Badge variant={benefit.isApplicant ? "default" : "secondary"}>
                          {benefit.isApplicant ? "Applicant" : "Household Member"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {statusLabels[benefit.status] ?? "Unknown"}
                      </TableCell>
                      <TableCell>{formatDate(benefit.intakeDate)}</TableCell>
                      <TableCell>
                        {formatDate(benefit.certificationDate)}
                      </TableCell>
                      <TableCell>
                        {benefit.applicantName || "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(benefit.amountPaid)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(benefit.amountEligible)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
